import { StyleGuideLite } from "@intelligems/ig-embedded-preview/messages";
import { messageProxyFactory } from "@src/features/experienceBuilder/messageProxyFactory";
import { PREVIEW_IFRAME_ID } from "@src/features/experienceBuilder/constants";
import { createAsyncThunk } from "@src/store/createAsyncThunk";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import type { CachedPageHtml } from "@src/store/slices/experienceBuilder/types";

export type PageHtmlResult = {
  snapshot: string;
  pageHtmlForPrompt: string;
  styleGuideLite: StyleGuideLite | null;
};

/**
 * Thrown by the thunk when capture exhausted retries AND no cached snapshot
 * was available. The caller (AI chat) catches this to abort the turn without
 * shipping an empty-HTML request to the server. The `pageHtmlUnavailable`
 * page-state surfaces the error UI with a reload action.
 */
export class PageHtmlUnavailableError extends Error {
  constructor() {
    super("Page HTML unavailable — preview did not respond in time.");
    this.name = "PageHtmlUnavailableError";
  }
}

// We keep pinging because the plugin drops `ig-getPageHtml` until both of
// its gates are open: plugin-lifecycle init done (`window.igFinished`) and
// embedded-preview capture-ready (`embedded.ready` in the plugin's own
// Redux store). A dropped request leaves nothing queued plugin-side, so
// the FE is the only party that can re-ask. The intra-window postMessage
// round-trip is microseconds; the wait is for the plugin to become ready.
export const PING_INTERVAL_MS = 300;
export const TOTAL_TIMEOUT_MS = 10_000;

/**
 * Promise-only core of the capture step — exported so tests can drive the
 * message proxy directly. Resolves on first `ig-pageHtml`, rejects after
 * `TOTAL_TIMEOUT_MS` of no response. The thunk wrapper layers cache lookup
 * and page-state dispatch on top.
 */
export const captureIframePageHtml = (): Promise<PageHtmlResult> =>
  new Promise<PageHtmlResult>((resolve, reject) => {
    const messageProxy = messageProxyFactory.get(PREVIEW_IFRAME_ID);
    if (!messageProxy) {
      reject(new PageHtmlUnavailableError());
      return;
    }

    let done = false;
    let pingTimer: ReturnType<typeof setInterval> | undefined;
    let deadline: ReturnType<typeof setTimeout> | undefined;

    const finish = (
      action: { ok: true; payload: PageHtmlResult } | { ok: false }
    ) => {
      if (done) {
        return;
      }
      done = true;
      if (pingTimer !== undefined) {
        clearInterval(pingTimer);
      }
      if (deadline !== undefined) {
        clearTimeout(deadline);
      }
      if (action.ok) {
        resolve(action.payload);
      } else {
        reject(new PageHtmlUnavailableError());
      }
    };

    messageProxy.on("ig-pageHtml", (payload) =>
      finish({ ok: true, payload: payload as PageHtmlResult })
    );
    messageProxy.send("ig-getPageHtml");
    pingTimer = setInterval(
      () => messageProxy.send("ig-getPageHtml"),
      PING_INTERVAL_MS
    );
    deadline = setTimeout(() => finish({ ok: false }), TOTAL_TIMEOUT_MS);
  });

const { selectors, actions } = experienceBuilderSlice;

export const requestPageHtml = createAsyncThunk<PageHtmlResult>(
  "experienceBuilder/requestPageHtml",
  async (_, { dispatch, getState }) => {
    try {
      const result = await captureIframePageHtml();
      // Successful capture invalidates any prior "unavailable" state and
      // refreshes the cache used as a fallback on future transient failures.
      // Style guide is intentionally not cached — it's cheap to recompute
      // and we only want to survive structural-state-stable blips.
      const cached: CachedPageHtml = {
        snapshot: result.snapshot,
        pageHtmlForPrompt: result.pageHtmlForPrompt,
      };
      dispatch(actions.setCachedPageHtml(cached));
      dispatch(actions.setPageHtmlUnavailable(false));
      return result;
    } catch (e) {
      if (!(e instanceof PageHtmlUnavailableError)) {
        throw e;
      }
      const cached = selectors.getCachedPageHtml(getState());
      if (cached) {
        // Degraded mode: ship the stale-but-recent snapshot rather than an
        // aborted turn. Style guide is omitted (null) so we don't lie about
        // its freshness.
        return { ...cached, styleGuideLite: null };
      }
      dispatch(actions.setPageHtmlUnavailable(true));
      throw e;
    }
  }
);
